import {useEffect, useMemo} from "react";

import {useQuery} from "@tanstack/react-query";

import {useStoredSession} from "@/src/modules/auth/store/session";
import {isViewingAsUser} from "@/src/modules/core/lib/impersonation";
import {useStoredMe} from "@/src/modules/core/store/me";

import {filterNotificationItemsForViewer} from "../lib/notification-feed-topics";
import {notificationFeedService} from "../services";
import {useNotificationFeedStore} from "../store";
import type {NotificationFeedQueryParams} from "../types";

export const NOTIFICATION_FEED_QUERY_KEY = "notification-feed";

type UseNotificationFeedParams = {
  params?: NotificationFeedQueryParams;
  enabled?: boolean;
};

const DEFAULT_PARAMS: NotificationFeedQueryParams = {
  unread: true,
  include_discarded: false,
  per_page: 25,
};

export function useNotificationFeed({params, enabled = true}: UseNotificationFeedParams = {}) {
  const session = useStoredSession();
  const me = useStoredMe();
  const viewingAsUser = isViewingAsUser(me);
  const {
    items,
    unreadCount,
    isHydrated,
    meta,
    hydrateFromApi,
    prependFromRealtime,
    removeByIdsFromDiscardEvent,
    removeShiftTopicItems,
    setHydrated,
  } = useNotificationFeedStore();

  const requestParams = useMemo(
    () => ({...DEFAULT_PARAMS, ...(params ?? {})}),
    [params],
  );

  const query = useQuery({
    queryKey: [NOTIFICATION_FEED_QUERY_KEY, requestParams],
    queryFn: () => notificationFeedService.getFeed(requestParams),
    retry: false,
    throwOnError: false,
    refetchInterval: 60_000,
    enabled: enabled && !!session,
  });

  useEffect(() => {
    if (!query.data) {
      return;
    }
    const data = filterNotificationItemsForViewer(query.data.data, viewingAsUser);
    hydrateFromApi({...query.data, data});
  }, [query.data, hydrateFromApi, viewingAsUser]);

  useEffect(() => {
    if (viewingAsUser) {
      removeShiftTopicItems();
    }
  }, [viewingAsUser, removeShiftTopicItems]);

  return {
    items,
    unreadCount,
    isHydrated,
    meta,
    refetch: query.refetch,
    isPending: query.isPending,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    prependFromRealtime,
    removeByIdsFromDiscardEvent,
    setHydrated,
  };
}
